/**
 * SSE stream parser for the agent endpoint.
 *
 * Parses the FastAPI SSE stream from /api/conversations/:id/messages
 * into typed SSEEvent objects. Handles reconnection, auth headers,
 * and the custom event types our backend emits.
 */

import type { SSEEvent } from "./types";
import { apiFetch } from "./client";

export interface StreamOptions {
  /** Called for each parsed SSE event */
  onEvent: (event: SSEEvent) => void;
  /** Called on unrecoverable error */
  onError?: (error: Error) => void;
  /** AbortSignal to cancel the stream */
  signal?: AbortSignal;
}

/**
 * Send a message and stream the agent's response as SSE events.
 *
 * Usage:
 * ```ts
 * const abort = new AbortController();
 * await streamMessage(conversationId, "show me comps near 376 Gradle Dr", {
 *   onEvent(event) {
 *     if (event.type === "token") appendText(event.text);
 *     if (event.type === "component") renderComponent(event.tool, event.data);
 *   },
 *   signal: abort.signal,
 * });
 * ```
 */
export async function streamMessage(
  conversationId: string,
  content: string,
  options: StreamOptions
): Promise<void> {
  const { onEvent, onError, signal } = options;

  let response: Response;
  try {
    response = await apiFetch(`/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    onError?.(err as Error);
    return;
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = body.detail;

    if (response.status === 401) {
      onEvent({ type: "error", error: "Session expired. Please sign in again." });
      return;
    }
    if (response.status === 429 && detail) {
      onEvent({
        type: "error",
        error: detail.message || "Rate limit reached. Please try again later.",
      });
      return;
    }
    if (response.status === 413 && detail) {
      onEvent({
        type: "error",
        error: detail.message || "Message is too long.",
      });
      return;
    }

    onEvent({
      type: "error",
      error: detail?.message || detail || `HTTP ${response.status}`,
    });
    return;
  }

  // Parse the SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    onError?.(new Error("No response body"));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  // 90s read timeout — if the backend goes silent (no events, no keepalive),
  // surface an error instead of hanging indefinitely.
  const READ_TIMEOUT_MS = 90_000;

  try {
    while (true) {
      const readWithTimeout = Promise.race([
        reader.read(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Stream timeout — no response from server")), READ_TIMEOUT_MS)
        ),
      ]);
      const { done, value } = await readWithTimeout;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          const rawData = line.slice(5).trim();
          const parsed = parseSSEEvent(currentEvent, rawData);
          if (parsed) onEvent(parsed);
        }
        // Ignore empty lines and comments
      }
    }
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    // Surface timeout as a user-visible error event
    if ((err as Error).message?.includes("Stream timeout")) {
      onEvent({ type: "error", error: "Response timed out. Please try again." });
      reader.cancel().catch(() => {});
      return;
    }
    onError?.(err as Error);
  }
}

/**
 * Parse a single SSE event+data pair into a typed SSEEvent.
 */
function parseSSEEvent(event: string, rawData: string): SSEEvent | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(rawData);
  } catch {
    return null;
  }

  switch (event) {
    case "token":
      return { type: "token", text: (data.text as string) || "" };

    case "thinking":
      return { type: "thinking", text: (data.text as string) || "" };

    case "tool_start":
      return {
        type: "tool_start",
        name: (data.name as string) || "unknown",
        args: (data.args as Record<string, unknown>) || data,
      };

    case "tool_result":
      return {
        type: "tool_result",
        name: (data.name as string) || "",
        result: (data.result as string) || "",
      };

    case "component":
      return {
        type: "component",
        tool: (data.tool as string) || "",
        data: (data.data as Record<string, unknown>) || {},
      };

    case "title_update":
      return { type: "title_update", title: (data.title as string) || "" };

    case "deal_context":
      return {
        type: "deal_context",
        deal_id: (data.deal_id as string) || "",
        parcel_id: data.parcel_id as string | undefined,
        address: data.address as string | undefined,
        status: (data.status as string) || "screening",
        title: data.title as string | undefined,
        completeness_score: (data.completeness_score as number) || 0,
      };

    case "dsr_update":
      return {
        type: "dsr_update",
        deal_id: (data.deal_id as string) || "",
        completeness_score: (data.completeness_score as number) || 0,
        pir_status: (data.pir_status as Record<string, string>) || {},
        ground_truth: (data.ground_truth as Record<string, unknown>) || {},
        estimates: (data.estimates as Record<string, unknown>) || {},
      };

    case "chips_update":
      return {
        type: "chips_update",
        chips: (data.chips as Array<{ label: string; message: string; category: string }>) || [],
      };

    case "usage":
      return { type: "usage", data: data as unknown as SSEEvent & { type: "usage" } extends { data: infer D } ? D : never };

    case "queued":
      return {
        type: "queued",
        position: (data.position as number) || 1,
        message: (data.message as string) || "Waiting for previous response...",
      };

    case "dequeued":
      return { type: "dequeued" };

    case "done":
      return { type: "done" };

    case "error":
      return { type: "error", error: (data.error as string) || "Unknown error" };

    default:
      console.warn(`Unknown SSE event: ${event}`, data);
      return null;
  }
}

